import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { DesignedJourney, DesignedJourneyStep } from '../../interfaces/models';
import { createDesignedJourney } from '../../services/gargi';
import { count } from '../Models/common';
import { actionError } from '../../../../shared/gargi/actionError';

const CHANNEL: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', rcs: 'RCS', webpush: 'Web push' };
const CONDITION: Record<string, string> = {
  not_buy: "Only those who haven't bought go on", not_delivered: "Only those it didn't reach go on",
  not_read: "Only those who didn't read it go on", not_clicked: "Only those who didn't click go on",
};

const describe = (step: DesignedJourneyStep, templates: Record<string, string>) => {
  switch (step.kind) {
    case 'message':
      return step.template_id
        ? `${CHANNEL[step.channel ?? ''] ?? step.channel} · template “${templates[step.template_id] ?? step.template_id}”`
        : `${CHANNEL[step.channel ?? ''] ?? step.channel} · new message (pick a template in the builder)`;
    case 'wait':
      return `Wait ${step.wait_days} day${step.wait_days === 1 ? '' : 's'}`;
    case 'offer':
      return `Offer ${step.offer_code}`;
    case 'loyalty':
      return `Award ${count(step.points ?? 0)} points${step.points_expiry_days ? `, valid ${step.points_expiry_days} days` : ''}`;
    case 'if':
      return CONDITION[step.condition ?? 'not_buy'] ?? 'Only some customers go on';
    default:
      return step.kind;
  }
};

// A journey designed in chat: the audience, the control group and each step, ready to open as a builder draft.
const JourneyCard: FC<{ journey: DesignedJourney; question?: string }> = ({ journey, question }) => {
  const navigate = useNavigate();
  const create = useMutation({
    mutationFn: () => createDesignedJourney(journey, question),
    onSuccess: ({ url }) => navigate(new URL(url).pathname),
  });
  return (
    <Paper variant='outlined' sx={{ p: 1.75, borderRadius: 2.5, borderColor: 'primary.main' }}>
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Typography variant='subtitle2' fontWeight={700}>{journey.name}</Typography>
        <Chip size='small' label='Journey' color='primary' variant='outlined' sx={{ height: 20 }} />
      </Stack>
      <Typography variant='body2' sx={{ mt: 0.75 }}>{journey.summary}</Typography>
      <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography variant='body2'>
          <strong>Audience:</strong> {journey.audience.description}{journey.audience.size ? ` · ${count(journey.audience.size)}` : ''}
        </Typography>
        {journey.control_percent > 0 && (
          <Typography variant='caption' color='text.secondary'>{journey.control_percent}% held back as a control group, to measure what the journey adds</Typography>
        )}
        <Stack component='ol' spacing={0.5} sx={{ pl: 2.5, mt: 1, mb: 0 }}>
          {journey.steps.map((step, i) => (
            <Box component='li' key={i}>
              <Typography variant='body2'>{describe(step, journey.templates)}</Typography>
              {step.kind === 'message' && step.message && !step.template_id && (
                <Typography variant='caption' color='text.secondary'>“{step.message}”</Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1.25 }}>
        <Button size='small' variant='contained' disableElevation disabled={create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create draft'}
        </Button>
        <Typography variant='caption' color='text.secondary'>Opens in the journey builder, ready to review and publish. Gargi there has this conversation, so you can keep changing it.</Typography>
      </Stack>
      {create.isError && (
        <Alert severity='error' sx={{ mt: 1 }}>{actionError(create.error, 'The draft could not be created.')}</Alert>
      )}
    </Paper>
  );
};

export default JourneyCard;
